/* eslint-disable no-underscore-dangle */
import * as Yup from 'yup';
import { uploadedFileSchema } from '../uploadedFile.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

export const travelerSchema = Yup.object().shape({
  lineItem: Yup.string().required('LineItem ID is required'),
  order: Yup.string().required('Order ID is required'),
  pPart: Yup.string().required('pPart ID is required'),
  project: Yup.string().required('Project ID is required'),
  shipped: Yup.boolean().default(false),
  /**
   * Written by shipTravelersInventory when marking a traveler shipped via the
   * shipping flow. Optional — travelers marked shipped manually won't have this.
   * Carried through to the SHIPPED inventory transaction for audit purposes.
   */
  shipmentId: Yup.string().nullable().default(null),
  /**
   * Customer order line item the traveler shipped against. This can differ from
   * lineItem when inventory from a replenishment batch ships to another order.
   */
  shippedLineItemId: Yup.string().nullable().default(null),
  /**
   * Set to true by updatedTravelerV2 (Case D) after the SHIPPED inventory
   * transaction is successfully recorded. Guards against duplicate transactions
   * on Cloud Function retries.
   */
  shippedInventoryRecorded: Yup.boolean().nullable().default(null),
  /**
   * Set by the updatedTravelerV2 cloud function when the traveler's last step
   * passes and a matching catalog product is found. Null means either the last
   * step has not passed or no matching product exists.
   * non-null value means inventory was recorded.
   */
  productDocId: Yup.string().nullable().default(null),
  productVersionId: Yup.string().nullable().default(null),
  /**
   * Doc IDs of every component lot (bag) installed on this traveler, flat
   * at the root so trace queries can use array-contains. Written by the
   * lot install flows alongside per-step detail.
   */
  componentLotIds: Yup.array().of(Yup.string()).default([]),
  steps: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required('Step type is required'),
      pPartId: Yup.string().nullable(),
      pPartDocId: Yup.string().nullable(),
      inspected: Yup.string().required('Inspected status is required'),
      inspectedNote: Yup.string().nullable(),
      runId: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required('Run ID is required'),
          project: Yup.string().required('Project ID is required'),
          pPart: Yup.string().nullable(),
          runID: Yup.number().required('Run Number is required'),
          start: Yup.mixed().required('Start time is required'),
          press: Yup.string().required('Press is required'),
          end: Yup.mixed().nullable(),
          cavity: Yup.string().nullable(),
        }),
      ).nullable(),
      /** Inspection-report files uploaded on inspection steps. */
      inspectionReport: Yup.array().of(uploadedFileSchema).nullable(),
      /**
       * Component bags installed at this step: each entry records the lot
       * and how much this traveler consumed from it (the bag's part and
       * code resolve from the lot doc). The flat root componentLotIds
       * carries the same lots for array-contains trace queries.
       */
      installedLots: Yup.array().of(
        Yup.object().shape({
          lotId: Yup.string().required('Installed lot ID is required'),
          /** 0 is legal: a scanned bag whose count couldn't supply the take. */
          quantity: Yup.number()
            .min(0, 'Installed quantity cannot be negative')
            .required('Installed quantity is required'),
        }),
      ).nullable(),
    }),
  ).default([]),
  tool: Yup.string().nullable(),
  _createdBy: Yup.object().shape({
    timestamp: YupFirestoreTimestamp.required(
      '_createdBy.timestamp is required',
    ),
  }).nullable().default(undefined),
  _updatedBy: Yup.object().shape({
    email: Yup.string().email().required('_updatedBy.email is required'),
    uid: Yup.string().required('_updatedBy.uid is required'),
    timestamp: YupFirestoreTimestamp,
  }).nullable().default(undefined),
});

export default class Traveler {
  constructor({
    id, lineItem, order, pPart, project, shipped,
    shipmentId, shippedLineItemId, shippedInventoryRecorded,
    productDocId, productVersionId,
    componentLotIds,
    steps, tool, _createdBy, _updatedBy,
  }) {
    const validatedData = travelerSchema.validateSync({
      lineItem,
      order,
      pPart,
      project,
      shipped,
      shipmentId,
      shippedLineItemId,
      shippedInventoryRecorded,
      productDocId,
      productVersionId,
      componentLotIds,
      steps,
      tool,
      _createdBy,
      _updatedBy,
    });

    this.id = id || null;
    this.lineItem = validatedData.lineItem;
    this.order = validatedData.order;
    this.pPart = validatedData.pPart;
    this.project = validatedData.project;
    this.shipped = validatedData.shipped || false;
    this.shipmentId = validatedData.shipmentId ?? null;
    this.shippedLineItemId = validatedData.shippedLineItemId ?? null;
    this.shippedInventoryRecorded = validatedData.shippedInventoryRecorded ?? null;
    this.productDocId = validatedData.productDocId ?? null;
    this.productVersionId = validatedData.productVersionId ?? null;
    this.componentLotIds = validatedData.componentLotIds || [];
    this.steps = validatedData.steps || [];
    this.tool = validatedData.tool || null;
    this._createdBy = validatedData._createdBy;
    this._updatedBy = validatedData._updatedBy || null;
  }

  toFirestore({ serverTimestamp }) {
    return {
      lineItem: this.lineItem,
      order: this.order,
      pPart: this.pPart,
      project: this.project,
      shipped: this.shipped,
      shipmentId: this.shipmentId,
      shippedLineItemId: this.shippedLineItemId,
      shippedInventoryRecorded: this.shippedInventoryRecorded,
      productDocId: this.productDocId,
      productVersionId: this.productVersionId,
      componentLotIds: this.componentLotIds,
      steps: this.steps,
      tool: this.tool,
      _createdBy: {
        timestamp: this._createdBy?.timestamp || serverTimestamp(),
      },
      _updatedBy: {
        email: this._updatedBy?.email || null,
        uid: this._updatedBy?.uid || null,
        timestamp: serverTimestamp(),
      },
    };
  }

  static fromFirestore(docData) {
    return new Traveler(docData);
  }
}
